/* eslint-disable react/jsx-one-expression-per-line */
import React, { useState, useEffect } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Spinner, Modal } from 'ui-component-library';

import MainContainer, {
  NavIcon,
  HeaderBar,
  HeaderPill,
  HeaderLink,
  GridContainer,
  GridTitle,
  ColumnContainer,
  ContentContainer,
  ModalContainer,
  Label,
  Value,
  LinkContainer,
  LockContainer,
  LockIcon,
  HeaderContent,
  LockContent,
  HeaderDetailsContainer,
  StyledLink,
  LoadNotFoundContainer,
  LoadNotFoundTitle,
  LoadNotFoundMessage,
  LoadNotFoundLink,
} from './load-details.style';
import HeaderDetailsItem from '../../core/common/header-details-item';
import LoadStatus from '../../core/common/card-load-status';
import { fetchLoadDetails } from '../../../store/load-details/thunkActions';
import ActivityHistory from '../../core/common/card-activity-history/card-activity-history';
import ProblemsWithLoad from '../../core/common/problems-with-load';
import TransitTracker from '../../core/tl/card-transit-tracker';
import LastHumanUpdate from '../../core/tl/card-last-human-update';
import TrackingActionsCard from '../../core/common/action-card-tracking-actions';
import DriverTruckInfo from '../../core/tl/card-driver-truck-info';
import ShipmentStops from '../../core/common/card-shipment-stops';
import LoadIdentifiersCard from '../../core/ltl/load-identifiers-card';
import navIcon from '../../../assets/icon-nav-arrow.svg';
import lockIcon from '../../../assets/icon-lock.svg';
import { clearLoadDetails } from '../../../store/load-details/slice';
import Tooltip from '../../core/common/tooltip/tooltip';
import useLockLoad from '../../../hooks/load-lock';
import { LOCK_EXPIRATION_STATES, MODE_TYPES, STATUS_TYPES } from '../../../constants';
import { clearSelectedLoad } from '../../../store/selected-load/slice';
import LockModal from '../../core/common/modals/lock-modal';
import { TMS_APP_URL } from '../../../env';
import { formatUsername, setDocumentTitle } from '../../../utils/common';

/**
 * Load Details component
 * @return {element} - The Load Details Container Component
 */
const LoadDetails = () => {
  // General hooks
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();

  // Redux hooks
  const dispatch = useDispatch();
  const loadDetails = useSelector((state) => state.loadDetails.data);
  const loadDetailsJob = useSelector((state) => state.loadDetails);
  const selectedLoad = useSelector((state) => state.selectedLoad);
  const modeType = useSelector((state) => state.modeType?.type);

  // State Hooks
  const [showLoadIdsModal, setShowLoadIdsModal] = useState(false);
  const [showLockExpiringModal, setShowLockExpiringModal] = useState(false);

  const tmsAppSalesOrderUrl = `${TMS_APP_URL}/tl/sales-order`;
  const tmsLtlAppSalesOrderUrl = `${TMS_APP_URL}/sales-order`;

  /**
    * Refreshes the load details data.
    * @param {object} overrideData - Object containing load properties to override on the refreshed data.
    * Used for properties in the index that don't update synchronously.
    * @return {void} - This function does not return anything.
    */
  const refetchData = (overrideData) => {
    const sbk = location?.state?.salesOrderBk;
    dispatch(fetchLoadDetails(id, sbk, overrideData));
    setShowLockExpiringModal(false);
  };

  const loadLock = useLockLoad(loadDetails, refetchData, () => setShowLockExpiringModal(true), true);

  useEffect(() => {
    const sbk = location?.state?.salesOrderBk;
    dispatch(fetchLoadDetails(id, sbk));
    return () => {
      dispatch(clearLoadDetails());
    };
  }, [dispatch, id, location]);

  useEffect(() => {
    if (loadDetails) {
      const { originState, destinationState } = loadDetails;
      setDocumentTitle(`${loadDetails.billOfLadingNumber} ${originState}-${destinationState}`);
    }
  }, [loadDetails]);

  /**
   * Handles when a user clicks the
   * 'Go Back to Tracking Queue' button
   * @param {boolean} retainstate - Boolean to signifiy if we want to return URL state when going back
   * @return {void}
   */
  const handleGoBack = (retainState = true) => {
    loadLock.clearTimeouts();
    // If a lock exists, and there is a selectedLoad in state that has
    // the same lockId as our loadLock, don't clear the lock.
    // This signifies we are returning to the home page with
    // the current locked load already selected, and we want to retain
    // it's current lock.
    if (loadLock && (selectedLoad?.id !== loadLock?.loadId)) {
      loadLock.clearLock();
    }
    // Reminder: Tracking queue is the only page that routes to details
    if (location?.state?.prevPath && retainState) {
      history.goBack();
    } else {
      history.push('/');
    }
  };

  /**
   * Renders the locked header state
   * @return {element} - Locked header state
   */
  const renderLockedState = () => {
    const lock = loadDetails.loadLock;
    const content = `This load is currently locked by ${formatUsername(lock.initiatorName)}`;
    return (
      <LockContainer>
        <LockIcon src={lockIcon} height="18px" width="18px" alt="lock" />
        <LockContent><strong>{content}</strong></LockContent>
      </LockContainer>

    );
  };

  /**
   * Closes the lock expiration modal.
   * If the expiring state of the lack at the given moment is expiring,
   * call the locks acknowledgeExpiration method to acknoledge that the lock
   * is expiring.
   * If the lock is expired, clear the loadSummary and selectedLoad
   * and ensure no timeouts are set on the current lock
   * to cause the peek view to close upstream.
   * @return {void}
   */
  const closeLockExpirationModal = () => {
    setShowLockExpiringModal(false);
    if (loadLock.expiring === LOCK_EXPIRATION_STATES.expiring) {
      loadLock.acknowledgeExpiration();
    } else if (loadLock.expiring === LOCK_EXPIRATION_STATES.expired) {
      dispatch(clearSelectedLoad());
      dispatch(clearLoadDetails());
      if (loadLock) {
        loadLock.clearTimeouts();
      }
      handleGoBack(false);
    }
  };

  /**
  * Render the lock expiration modal
  * @param {object} lock - Lock Object
  * @param {object} summary - Load summary object
  */
  const renderLockExpirationModal = (lock, summary) => {
    // If the lock is in expiring state, show the expiring modal
    const expired = lock.expiring === LOCK_EXPIRATION_STATES.expired;
    return (
      <LockModal
        showModal={showLockExpiringModal}
        closeModal={closeLockExpirationModal}
        resetTimer={() => loadLock.extendLock(refetchData)}
        loadInfo={{
          ...summary,
          loadLock: lock,
        }}
        timerExpired={expired}
        lock={loadLock}
      />
    );
  };

  const getReferenceIdentifiers = (refIds) => {
    const mapToLabel = (key) => {
      switch (key) {
        case 'proNumber':
          return 'Pro Number';
        case 'puNumber':
          return 'PU Number';
        case 'refNumber':
          return 'Reference Number';
        case 'poNumber':
          return 'PO Number';
        case 'customerBol':
          return 'Customer BOL';
        case 'interlineCarrier':
          return 'Interline Carrier';
        case 'interlineProNumber':
          return 'interlineProNumber';
        case 'invoiceNumber':
          return 'Invoice Number';
        case 'quoteNumber':
          return 'Quote Number';
        case 'bolNumber':
          return 'BOL Number';
        default:
          return '';
      }
    };

    if (refIds === null) {
      return [];
    }
    return Object.keys(refIds).map((key) => (
      {
        label: mapToLabel(key),
        value: refIds[key],
        key,
      }
    ));
  };

  // If the loadDetails job failed and returned a 404, show the
  // load not found message.
  if (loadDetailsJob.status === STATUS_TYPES.FAILED_STATE && loadDetailsJob.error.status === 404) {
    return (
      <LoadNotFoundContainer>
        <LoadNotFoundTitle>Whoops... load not found!</LoadNotFoundTitle>
        <LoadNotFoundMessage>
          This load can no longer be found in Track & Trace due to its current status.
          Please return to the <LoadNotFoundLink href="/">Tracking Queue</LoadNotFoundLink> to access other loads.
        </LoadNotFoundMessage>
      </LoadNotFoundContainer>
    );
  }

  /**
   * If the Load Details is not defined yet, show the spinner overlay
   */
  if (loadDetails?.loadId !== `${id}`) {
    return <Spinner overlay zIndex={100} />;
  }

  const locked = loadLock.loadId && !loadLock.ownedByUser;
  const refIds = getReferenceIdentifiers(loadDetails?.referenceIdentifiers);
  return (
    <MainContainer>
      {(loadDetails && loadLock) && renderLockExpirationModal(loadLock, loadDetails)}
      <HeaderContent locked={locked}>
        <LinkContainer>
          <HeaderLink onClick={handleGoBack}>
            <NavIcon src={navIcon} />
            Back to Tracking Queue
          </HeaderLink>
        </LinkContainer>
        {locked && renderLockedState()}
        <LinkContainer>
          <StyledLink href={`${modeType === MODE_TYPES.truckload ? tmsAppSalesOrderUrl : tmsLtlAppSalesOrderUrl}/${loadDetails?.billOfLadingNumber}`} target="_blank">
            View Sales Order in TMS
          </StyledLink>
        </LinkContainer>
      </HeaderContent>
      <HeaderBar>
        <HeaderDetailsContainer>
          <HeaderDetailsItem
            label="LoadID#"
            item={loadDetails.billOfLadingNumber}
          />
          <HeaderDetailsItem
            label="Customer"
            item={loadDetails.customerName}
          />
          <HeaderDetailsItem
            label="Carrier Rep"
            item={loadDetails.carrierRepName}
          />
          <HeaderDetailsItem
            label="Carrier"
            item={loadDetails.carrierName}
          />
        </HeaderDetailsContainer>
        {loadDetails.commodity && (
          <Tooltip
            content={loadDetails.commodity}
            position="bottom"
            delay={0.2}
          >
            <HeaderPill>
              {loadDetails.commodity}
            </HeaderPill>
          </Tooltip>
        )}
        {loadDetails?.freightIndicators?.map((freightIndicator) => (
          <HeaderPill key={freightIndicator}>
            {freightIndicator}
          </HeaderPill>
        ))}
        <HeaderLink onClick={() => setShowLoadIdsModal(true)}>
          See Load Identifiers
        </HeaderLink>
      </HeaderBar>
      <ContentContainer>
        <GridContainer>
          <ColumnContainer>
            <GridTitle>Status &amp; Route</GridTitle>
            <LoadStatus
              loadStatus={modeType === MODE_TYPES.truckload ? loadDetails.tlStatus : loadDetails.ltlStatus}
              processStatus={loadDetails.processStatus}
              mode={modeType}
            />
            {loadDetails.problems?.length > 0 && <ProblemsWithLoad loadProblems={loadDetails.problems} />}
            <TransitTracker
              load={loadDetails}
              lastTrackingEvent={loadDetails.lastTrackingEvent}
              refetchLoad={refetchData}
            />
            <LastHumanUpdate
              lastUpdate={loadDetails.lastHumanEvent}
              lastTrack={loadDetails.lastTrackingEvent}
            />
            <TrackingActionsCard
              load={loadDetails}
              refetchLoad={refetchData}
              lock={loadLock}
              mode={modeType}
            />
          </ColumnContainer>
          <ColumnContainer>
            <GridTitle>Load Management</GridTitle>
            {modeType === MODE_TYPES.lessThanTruckload && (
              <LoadIdentifiersCard
                lock={loadLock}
                load={loadDetails}
                refetchLoad={refetchData}
              />
            )}
            {modeType === MODE_TYPES.truckload && (
              <DriverTruckInfo
                lock={loadLock}
                load={loadDetails}
                refetchLoad={refetchData}
              />
            )}
            <ShipmentStops
              data={loadDetails.shipmentInformation}
              loadId={loadDetails.loadId}
              status={modeType === MODE_TYPES.truckload ? loadDetails.tlStatus : loadDetails.ltlStatus}
              salesOrderBk={loadDetails.billOfLadingNumber}
              refetchData={refetchData}
              mode={modeType}
            />
          </ColumnContainer>
          <ColumnContainer>
            <GridTitle>Activity</GridTitle>
            <ActivityHistory activities={loadDetails.activity} />
          </ColumnContainer>
        </GridContainer>
      </ContentContainer>
      <Modal
        title="Additional Load Identifiers"
        isOpen={showLoadIdsModal}
        onModalClose={() => setShowLoadIdsModal(false)}
        width="320px"
      >
        <ModalContainer>
          <Label>Load ID</Label>
          <Value>{loadDetails.loadId ? `#${loadDetails.billOfLadingNumber}` : ''}</Value>
          {loadDetails.additionalIds?.length > 0 && loadDetails.additionalIds.map((identifier, i) => (
            <React.Fragment key={i}>
              <Label>{identifier.label}</Label>
              <Value>{identifier.value ? `#${identifier.value}` : ''}</Value>
            </React.Fragment>
          ))}
          {refIds.map((refId) => (
            <>
              <Label>{refId.label}</Label>
              <Value>{refId.value}</Value>
            </>
          ))}
        </ModalContainer>
      </Modal>
    </MainContainer>
  );
};

export default LoadDetails;
